const PARTICLE_COUNT = 80;
const SPREAD = 360;
const GRAVITY = 0.08;
const DRAG = 0.98;
const COLORS = [
  "#ff4405", // redo orange
  "#ff6b3d",
  "#ffa726",
  "#ffd54f",
  "#66bb6a",
  "#42a5f5",
  "#ab47bc",
  "#ec407a",
];

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  width: number;
  height: number;
  color: string;
  rotation: number;
  rotationSpeed: number;
  opacity: number;
}

function randomBetween(min: number, max: number): number {
  return Math.random() * (max - min) + min;
}

/**
 * Fire a confetti burst from the center of the viewport. Creates a temporary
 * full-screen canvas, animates particles, then removes itself.
 *
 * Returns a cleanup function that immediately cancels the animation and
 * removes the canvas (for use in React useEffect teardown).
 */
export function fireConfetti(): () => void {
  const canvas = document.createElement("canvas");
  canvas.style.position = "fixed";
  canvas.style.inset = "0";
  canvas.style.zIndex = "1699";
  canvas.style.pointerEvents = "none";
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;
  document.body.append(canvas);

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    canvas.remove();
    return () => {};
  }

  const cx = canvas.width / 2;
  const cy = canvas.height / 2;

  const particles: Particle[] = Array.from(
    { length: PARTICLE_COUNT },
    (): Particle => {
      const angle = randomBetween(0, SPREAD) * (Math.PI / 180);
      const speed = randomBetween(4, 12);
      return {
        x: cx,
        y: cy,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - randomBetween(2, 6),
        width: randomBetween(4, 8),
        height: randomBetween(6, 12),
        color: COLORS[Math.floor(Math.random() * COLORS.length)] ?? "#ff4405",
        rotation: randomBetween(0, Math.PI * 2),
        rotationSpeed: randomBetween(-0.2, 0.2),
        opacity: 1,
      };
    },
  );

  let frame: number;
  let elapsed = 0;

  function draw() {
    elapsed++;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    ctx!.clearRect(0, 0, canvas.width, canvas.height);

    let alive = false;
    for (const p of particles) {
      if (p.opacity <= 0) {
        continue;
      }
      alive = true;

      p.x += p.vx;
      p.y += p.vy;
      p.vy += GRAVITY;
      p.vx *= DRAG;
      p.vy *= DRAG;
      p.rotation += p.rotationSpeed;

      // Fade out after 60 frames
      if (elapsed > 60) {
        p.opacity = Math.max(0, p.opacity - 0.02);
      }

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ctx!.save();
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ctx!.translate(p.x, p.y);
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ctx!.rotate(p.rotation);
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ctx!.globalAlpha = p.opacity;
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ctx!.fillStyle = p.color;
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ctx!.fillRect(-p.width / 2, -p.height / 2, p.width, p.height);
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ctx!.restore();
    }

    if (alive) {
      frame = requestAnimationFrame(draw);
    } else {
      canvas.remove();
    }
  }

  frame = requestAnimationFrame(draw);

  // Safety net: remove after 4 seconds regardless
  const safetyTimer = setTimeout(() => {
    cancelAnimationFrame(frame);
    canvas.remove();
  }, 4000);

  return () => {
    clearTimeout(safetyTimer);
    cancelAnimationFrame(frame);
    canvas.remove();
  };
}
